import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { Button, Paper } from '@mui/material';
import { useState } from 'react';
import { DataGrid, DataGridSortModel } from '@repo/components';
import GenericDataPageHeader from 'src/components/GenericDataPageHeader';
import { DocumentType } from '@repo/shared/domain';
import { AddDocumentTypeForm as AddDocumentType, type AddDocumentTypeFormData as FormData } from '@repo/forms';
import { EventEmitter, constructQuery, sortToString, stringToSort } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { getAllDocumentTypes } from 'src/api/admin/documentTypes';
import { createDocumentType, updateDocumentType } from 'src/api/admin/documentTypes';
import { useConfig } from 'src/hooks/useConfig';

interface DocumentTypesSearch {
	page?: number;
	sort?: string;
}

export const Route = createFileRoute('/_admin/system/document-types')({
	validateSearch: (search: Record<string, unknown>): DocumentTypesSearch => ({
		page: search.page as number | undefined,
		sort: search.sort as string | undefined,
	}),
	loaderDeps: ({ search: { page, sort } }) => ({ page, sort }),
	loader: async ({ deps }) => {
		const queryParams = {
			page: deps.page,
			sort: deps.sort || 'name:asc',
		};
		const query = constructQuery(queryParams);
		const documentTypes = await getAllDocumentTypes(query);
		return { documentTypes };
	},
	component: DocumentTypesPage,
});

const columns = [
	{ field: 'name', headerName: 'Name', flex: 1 },
	{ field: 'description', headerName: 'Description', flex: 2 },
	{
		field: 'sensitive',
		headerName: 'Sensitive',
		flex: 1,
		renderCell: (cell: any) => (cell.value ? <CheckIcon /> : ''),
	},
	{
		field: 'defaultInReview',
		headerName: 'Default In Review',
		flex: 1,
		renderCell: (cell: any) => (cell.value ? <CheckIcon /> : ''),
	},
	{
		field: 'hasExpiryDate',
		headerName: 'Has Expiry Date',
		flex: 1,
		renderCell: (cell: any) => (cell.value ? <CheckIcon /> : ''),
	},
	{ field: 'parsingMethod', headerName: 'Parsing Method', flex: 1 },
];

function DocumentTypesPage() {
	const { page, sort } = Route.useSearch();
	const { documentTypes: initialDocumentTypes } = Route.useLoaderData();
	const queryClient = useQueryClient();
	const updateQueryParams = useUpdateQueryParams();
	const { config } = useConfig();
	const featureFlags = config?.featureFlags;

	const queryParams = {
		page,
		sort: sort || 'name:asc',
	};
	const query = constructQuery(queryParams);

	const { data: documentTypes, refetch: revalidateDocuments } = useQuery({
		queryKey: ['document-types', query],
		queryFn: () => getAllDocumentTypes(query),
		initialData: initialDocumentTypes,
	});

	const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
	const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType | null>(null);

	const defaultDocumentTypeData = {
		defaultInReview: true,
		hasExpiryDate: true,
	};

	useBreadcrumbLevel(0, 'System');
	useBreadcrumbLevel(1, 'Document Types');

	const handlePaginationModelChange = ({ page }: { page: number }) => {
		updateQueryParams({ page });
	};

	const handleRowClick = (row: DocumentType) => {
		console.log(row);
		setSelectedDocumentType(row);
		setIsDialogOpen(true);
	};

	const handleSort = ([sortModel]: DataGridSortModel) => {
		updateQueryParams({ sort: sortModel ? sortToString(sortModel) : undefined });
	};

	const handleAddDocumentType = () => {
		setIsDialogOpen(true);
	};

	const handleSubmitDocumentType = async (data: FormData) => {
		try {
			if (data.id) {
				await updateDocumentType(data.id, data);
			} else {
				await createDocumentType(data);
			}

			revalidateDocuments();
			queryClient.invalidateQueries({ queryKey: ['document-types'] });
		} catch (err: any) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: err.status === 405 ? err.message : 'Error creating document type',
			});
		}

		setIsDialogOpen(false);
	};

	const handleCloseDialog = () => {
		setIsDialogOpen(false);
		setSelectedDocumentType(null);
	};

	const actions = [
		<Button key="add" onClick={handleAddDocumentType} variant="outlined" startIcon={<AddIcon />}>
			Add Document Type
		</Button>,
	];

	return (
		<>
			<GenericDataPageHeader actions={actions} />
			<div className="p-8">
				<Paper variant="outlined">
					<DataGrid
						data={documentTypes.data}
						page={documentTypes.page}
						pageSize={documentTypes.pageSize}
						pageSizeOptions={[documentTypes.pageSize]}
						rowCount={documentTypes.count || 0}
						columns={columns}
						paginationMode="server"
						sortingMode="server"
						onRowClick={(row: any) => handleRowClick(row.row)}
						sortingOrder={['asc', 'desc']}
						sortModel={[stringToSort(sort || 'name:asc')]}
						onSortModelChange={handleSort}
						onReload={revalidateDocuments}
						onPaginationModelChange={handlePaginationModelChange}
					/>
				</Paper>
			</div>
			{isDialogOpen && (
				<AddDocumentType
					originalValues={{ ...defaultDocumentTypeData, ...selectedDocumentType }}
					isOpen={isDialogOpen}
					onClose={handleCloseDialog}
					onSubmit={handleSubmitDocumentType}
					featureFlags={featureFlags}
				/>
			)}
		</>
	);
}
